import React, { useState, useEffect } from "react";
import styled, { keyframes, createGlobalStyle } from "styled-components";
import image1 from "../images/1s.png";
import image2 from "../images/2s.png";
import image3 from "../images/3s.png";
import image4 from "../images/4s.png";
import successStoryBackground from "../images/successStoryback.png";

// Global font import for Poppins
const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');
`;

// Keyframes for continuous left-to-right scrolling (opposite to reviews)
const scrollLeftToRight = keyframes`
  0% {
    transform: translateX(-50%);
  }
  100% {
    transform: translateX(0);
  }
`;

const Container = styled.div`
  font-family: "Poppins", sans-serif;
  background-color: #000;
  color: #fff;
  min-height: 80vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px 0;
  
  @media (min-width: 320px) and (max-width: 480px) {
    padding: 15px 0;
  }
`;

const ContentWrapper = styled.div`
  background-image: url(${successStoryBackground});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 15px;
  overflow: hidden;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  position: relative;
`;

const HeaderSection = styled.div`
  padding: 60px 20px 40px 20px;
  text-align: center;
  background: rgba(0, 0, 0, 0.4);
  position: relative;
`;

const Heading = styled.h2`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 10px;
  color: #fff;
  font-family: "Poppins", sans-serif;
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const SubText = styled.p`
  font-size: 14px;
  color: #ccc;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
  text-align: center;
  font-family: "Poppins", sans-serif;
`;

const CarouselSection = styled.div`
  padding: 40px 0 60px 0;
  position: relative;
  overflow: hidden;
  width: 100%;
`;

const ScrollContainer = styled.div`
  width: 100%;
  overflow: hidden;
  position: relative;
`;

const ScrollTrack = styled.div`
  display: flex;
  animation: ${scrollLeftToRight} 30s linear infinite;
  width: calc(300px * ${props => props.cardCount * 2}); // Double the width for seamless loop
  
  &:hover {
    animation-play-state: paused;
  }
`;

const Card = styled.div`
  background: #111;
  padding: 20px;
  border-radius: 10px;
  width: 270px;
  flex-shrink: 0;
  margin: 0 15px;
  text-align: left;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.4);
  }
  
  @media (max-width: 768px) {
    width: 240px;
    padding: 16px;
    margin: 0 12px;
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: 250px;
  object-fit: cover;
  border-radius: 8px;
  margin-bottom: 15px;
  background: #333;
  
  @media (max-width: 768px) {
    height: 200px;
  }
`;

const CardName = styled.div`
  font-size: 18px;
  font-weight: 600;
  margin: 10px 0 5px;
  color: #fff;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const CardStars = styled.div`
  color: #f97316;
  margin-bottom: 10px;
  font-size: 16px;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const CardQuote = styled.div`
  font-size: 14px;
  line-height: 1.5;
  color: #aaa;
  
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;
const SuccessStories = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  // Window resize handler
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-scroll functionality for mobile views
  useEffect(() => {
    if (windowWidth >= 320 && windowWidth <= 480) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % members.length);
      }, 3000); // Auto-scroll every 3 seconds

      return () => clearInterval(interval);
    }
  }, [windowWidth]);

  const members = [
    {
      name: "Derek",
      image: image1,
      quote: "Future Fitness transformed my life! Lost 30 pounds and gained incredible strength in just 6 months."
    },
    {
      name: "Anthony",
      image: image2,
      quote: "The trainers here are amazing. They helped me build muscle and confidence I never knew I had."
    },
    {
      name: "John",
      image: image3,
      quote: "Best gym experience ever! The community and support system here is unmatched."
    },
    {
      name: "Ezekiel",
      image: image4,
      quote: "From zero to hero! This gym gave me the tools and motivation to achieve my dream physique."
    },
  ];

  // Double the members array for seamless infinite scroll
  const duplicatedMembers = [...members, ...members];

  // For larger screens, show the new animated cards
  if (windowWidth > 480) {
    return (
      <>
        <GlobalStyle />
        <Container>
          <ContentWrapper>
            <HeaderSection>
              <Heading>Member's Success Story</Heading>
              <SubText>
                Our dedicated members have achieved incredible transformations through hard work and commitment.
                Their inspiring journeys showcase the power of determination and the results that come from consistent training.
              </SubText>
            </HeaderSection>
            <CarouselSection>
              <ScrollContainer>
                <ScrollTrack cardCount={members.length}>
                  {duplicatedMembers.map((member, index) => (
                    <Card key={`${member.name}-${index}`}>
                      <CardImage src={member.image} alt={member.name} />
                      <CardName>{member.name}</CardName>
                      <CardStars>★★★★★</CardStars>
                      <CardQuote>"{member.quote}"</CardQuote>
                    </Card>
                  ))}
                </ScrollTrack>
              </ScrollContainer>
            </CarouselSection>
          </ContentWrapper>
        </Container>
      </>
    );
  }
  // For mobile screens, keep the existing mobile implementation
  return (
    <>
      <GlobalStyle />
      <style>
        {`
          @media (min-width: 320px) and (max-width: 480px) {
            .mobile-container {
              background-image: none !important;
              padding: 15px 0 !important;
            }
            
            .mobile-header-section {
              padding: 35px 15px 25px 15px !important;
              background: #000 !important;
              text-align: center !important;
            }
            
            .mobile-carousel-section {
              background-image: url(${successStoryBackground}) !important;
              background-size: cover !important;
              background-position: center !important;
              background-repeat: no-repeat !important;
              padding: 35px 15px 50px 15px !important;
              position: relative !important;
            }
            
            .mobile-carousel-section::before {
              content: '' !important;
              position: absolute !important;
              top: 0 !important;
              left: 0 !important;
              right: 0 !important;
              bottom: 0 !important;
              background: rgba(0, 0, 0, 0.4) !important;
              z-index: 1 !important;
            }
            
            .mobile-carousel-container {
              overflow: hidden !important;
              width: 100% !important;
              padding: 0 !important;
              position: relative !important;
              z-index: 2 !important;
            }
            
            .mobile-cards-wrapper {
              display: flex !important;
              transition: transform 0.5s ease-in-out !important;
              align-items: center !important;
              justify-content: flex-start !important;
            }
            
            .mobile-card {
              flex: 0 0 240px !important;
              margin: 0 15px !important;
              background: #111 !important;
              padding: 12px !important;
              border-radius: 8px !important;
              text-align: left !important;
              transition: opacity 0.5s ease-in-out, transform 0.5s ease-in-out !important;
              opacity: 0.4 !important;
              transform: scale(0.85) !important;
              position: relative !important;
              z-index: 3 !important;
            }
            
            .mobile-card.center-card {
              opacity: 1 !important;
              transform: scale(1) !important;
              z-index: 10 !important;
            }
            
            .mobile-card img {
              width: 100% !important;
              height: 160px !important;
              object-fit: cover !important;
              border-radius: 6px !important;
              margin-bottom: 8px !important;
            }
            
            .mobile-card-name {
              font-size: 14px !important;
              font-weight: 600 !important;
              margin: 6px 0 4px !important;
              color: #fff !important;
            }
            
            .mobile-card-stars {
              color: #f97316 !important;
              margin-bottom: 6px !important;
              font-size: 12px !important;
            }
            
            .mobile-card-quote {
              font-size: 10px !important;
              line-height: 1.3 !important;
              color: #aaa !important;
            }
            
            .mobile-heading {
              font-size: 20px !important;
              font-weight: 700 !important;
              margin-bottom: 12px !important;
              color: #fff !important;
            }
            
            .mobile-subtext {
              font-size: 11px !important;
              color: #ccc !important;
              max-width: 92% !important;
              margin: 0 auto !important;
              line-height: 1.4 !important;
              text-align: center !important;
              padding: 0 5px !important;
            }
          }
          
          @media (max-width: 360px) {
            .mobile-container {
              padding: 10px 0 !important;
            }
            
            .mobile-header-section {
              padding: 30px 10px 20px 10px !important;
            }
            
            .mobile-carousel-section {
              padding: 30px 10px 40px 10px !important;
            }
            
            .mobile-card {
              flex: 0 0 220px !important;
              margin: 0 12px !important;
              padding: 10px !important;
            }
            
            .mobile-card img {
              height: 140px !important;
            }
            
            .mobile-card-name {
              font-size: 13px !important;
            }
            
            .mobile-card-stars {
              font-size: 11px !important;
            }
            
            .mobile-card-quote {
              font-size: 9px !important;
            }
            
            .mobile-heading {
              font-size: 18px !important;
              margin-bottom: 10px !important;
            }
            
            .mobile-subtext {
              font-size: 10px !important;
              max-width: 95% !important;
            }
          }
        `}
      </style>
      <div
        style={{
          fontFamily: "Poppins, sans-serif",
          backgroundColor: "#000",
          color: "#fff",
          minHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "15px 0"
        }}
        className="mobile-container"
      >
        <div className="mobile-header-section">
          <div className="mobile-heading">Member's Success Story</div>
          <p className="mobile-subtext">
            Our dedicated members have achieved incredible transformations through hard work and commitment.
            Their inspiring journeys showcase the power of determination and the results that come from consistent training.
          </p>
        </div>
        <div className="mobile-carousel-section">
          <div className="mobile-carousel-container">
            <div
              className="mobile-cards-wrapper"
              style={{
                transform: `translateX(calc(50% - ${(currentIndex * (240 + 30)) + 120}px))`
              }}
            >
              {/* Render cards multiple times for infinite loop effect */}
              {[...members, ...members, ...members].map((member, index) => (
                <div
                  key={index}
                  className={`mobile-card ${index % members.length === currentIndex ? 'center-card' : ''}`}
                >
                  <img src={member.image} alt={member.name} />
                  <div className="mobile-card-name">{member.name}</div>
                  <div className="mobile-card-stars">★★★★★</div>
                  <div className="mobile-card-quote">
                    "{member.quote}"
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SuccessStories;
